import React, { useState } from "react";
import {
  Grid,
  MenuItem,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import RestaurantOutlined from "@mui/icons-material/RestaurantOutlined";
import EmailOutlined from "@mui/icons-material/EmailOutlined";
import LockOutlined from "@mui/icons-material/LockOutlined";
import LocationOnOutlined from "@mui/icons-material/LocationOnOutlined";
import PublicOutlined from "@mui/icons-material/PublicOutlined";
import MapOutlined from "@mui/icons-material/MapOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { COUNTRIES, SRI_LANKA_DISTRICTS } from "../../../appConst";
import FormField from "./FormField";
import PhoneField from "./PhoneField";

const RestaurantInfoStep = ({
  formData,
  handleChange,
  handlePhoneChange,
  phoneError,
}) => {
  const theme = useTheme();
  const [showPassword, setShowPassword] = useState(false);

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Grid container spacing={3}>
      <FormField
        label="Restaurant Name"
        name="name"
        value={formData.name}
        onChange={handleChange("name")}
        placeholder="e.g., The Golden Fork"
        icon={<RestaurantOutlined sx={{ color: "#F9735B" }} />}
        required
        gridSize={{ xs: 12, md: 6 }}
      />
      <PhoneField
        value={formData.phone}
        onChange={handlePhoneChange}
        error={phoneError}
        gridSize={{ xs: 12, md: 6 }}
      />

      <FormField
        label="Email Address"
        name="email"
        type="email"
        value={formData.email}
        onChange={handleChange("email")}
        placeholder="contact@restaurant.com"
        icon={<EmailOutlined sx={{ color: "#F9735B" }} />}
        required
        validators={["isEmail"]}
        errorMessages={["Invalid email"]}
        gridSize={{ xs: 12, md: 6 }}
      />

      <FormField
        label="Password"
        name="password"
        type={showPassword ? "text" : "password"}
        value={formData.password}
        onChange={handleChange("password")}
        placeholder="Enter a secure password"
        icon={<LockOutlined sx={{ color: "#F9735B" }} />}
        endIcon={
          <IconButton
            onClick={handleTogglePasswordVisibility}
            edge="end"
            sx={{
              color: "#F9735B",
              "&:hover": {
                backgroundColor: "rgba(249, 115, 91, 0.1)",
              },
            }}
          >
            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
          </IconButton>
        }
        required
        validators={["required", "minStringLength:6"]}
        errorMessages={[
          "Password is required",
          "Password must be at least 6 characters",
        ]}
        gridSize={{ xs: 12, md: 6 }}
      />

      <Grid item size={12}>
        <Accordion
          expanded={true}
          disableGutters
          sx={{
            borderRadius: "12px !important",
            border: "1px solid #D9E2EC",
            boxShadow: "none",
            "&:before": { display: "none" },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#64748B" }} />}>
            <Typography sx={{ fontWeight: 700, fontSize: "0.86rem", color: "#334155", fontFamily: '"exo", "Inter", sans-serif' }}>
              Optional location details
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Grid container spacing={3}>
              <FormField
                label="Address (Optional)"
                name="address"
                value={formData.address}
                onChange={handleChange("address")}
                placeholder="123 Main Street"
                icon={<LocationOnOutlined sx={{ color: "#F9735B" }} />}
                multiline
                rows={1}
                gridSize={12}
              />

              <FormField
                label="Country (Optional)"
                name="country"
                value={formData.country}
                onChange={handleChange("country")}
                placeholder="Select a country"
                icon={<PublicOutlined sx={{ color: "#F9735B" }} />}
                select
                gridSize={{ xs: 12, md: 6 }}
                SelectProps={{
                  displayEmpty: true,
                  MenuProps: {
                    PaperProps: {
                      sx: {
                        "& .MuiMenuItem-root": {
                          fontFamily: theme.typography.fontFamily,
                          fontSize: "0.95rem",
                        },
                      },
                    },
                  },
                }}
              >
                <MenuItem value="" disabled sx={{ fontFamily: theme.typography.fontFamily, display: "none" }}>
                  Select a country
                </MenuItem>
                {COUNTRIES.map((country) => (
                  <MenuItem key={country.value} value={country.value} sx={{ fontFamily: theme.typography.fontFamily }}>
                    {country.label}
                  </MenuItem>
                ))}
              </FormField>

              {formData.country === "Sri Lanka" ? (
                <FormField
                  label="City / District (Optional)"
                  name="district"
                  value={formData.district}
                  onChange={handleChange("district")}
                  placeholder="Select a district"
                  icon={<MapOutlined sx={{ color: "#F9735B" }} />}
                  select
                  gridSize={{ xs: 12, md: 6 }}
                  SelectProps={{
                    displayEmpty: true,
                    MenuProps: {
                      PaperProps: {
                        sx: {
                          "& .MuiMenuItem-root": {
                            fontFamily: theme.typography.fontFamily,
                            fontSize: "0.95rem",
                          },
                        },
                      },
                    },
                  }}
                >
                  <MenuItem value="" disabled sx={{ fontFamily: theme.typography.fontFamily, display: "none" }}>
                    Select a district
                  </MenuItem>
                  {SRI_LANKA_DISTRICTS.map((district) => (
                    <MenuItem key={district.value} value={district.value} sx={{ fontFamily: theme.typography.fontFamily }}>
                      {district.label}
                    </MenuItem>
                  ))}
                </FormField>
              ) : (
                <FormField
                  label="City / District (Optional)"
                  name="district"
                  value={formData.district}
                  onChange={handleChange("district")}
                  placeholder="Enter district"
                  icon={<MapOutlined sx={{ color: "#F9735B" }} />}
                  gridSize={{ xs: 12, md: 6 }}
                />
              )}
            </Grid>
          </AccordionDetails>
        </Accordion>
      </Grid>
    </Grid>
  );
};

export default RestaurantInfoStep;

